import { useEffect, useState, useRef } from "react";
import InputField from "../../common/InputField";
import { unitConversionAPI } from "../../utlis/apiService";
import { FormFooter } from "../../common/Shared";
import Model from "../../common/Model";
import { MdAdd, MdDelete } from "react-icons/md";

const PackingForm = ({
  initialData,
  onSave,
  onCancel,
  isEditing,
  setFormData,
  formData,
  isSaving = false,
}) => {
  const today = new Date().toISOString()?.split("T")[0];
  const [isUnitConvData, setIsUnitConvData] = useState([]);
  const selectedUnit = isUnitConvData.find((u) => String(u.value) === String(formData.unit_Conv_Id));

  const tableBottomRef = useRef(null);
  const rowCount = formData?.packingDetails?.length || 0;
  const [prevRowCount, setPrevRowCount] = useState(rowCount);

  useEffect(() => {
    if (rowCount > prevRowCount) {
      setTimeout(() => {
        tableBottomRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
      }, 100);
    }
    setPrevRowCount(rowCount);
  }, [rowCount, prevRowCount]);

  useEffect(() => {
    const fetchUnitConvData = async () => {
      try {
        const res = await unitConversionAPI.getAll();
        setIsUnitConvData(
          res.data.map((u) => ({
            value: u.unit_Conv_Id,
            label: u.unit_Conv_Name,
            raw: u.conv_Value,
          })),
        );
      } catch (err) {
        console.error("Error loading dropdowns", err);
      }
    };
    fetchUnitConvData();
  }, []);

  const runCalculations = (currentFormData, unitsList) => {
    const pcs = parseFloat(currentFormData.pcs || 0);
    const totalWeight = parseFloat(currentFormData.total_Weight || 0);
    const wtCutLength = parseFloat(currentFormData.wt_Cut_Length || 0);

    const updated = { ...currentFormData };

    // final_CutLength_weight = total_Weight / pcs
    const finalCutLengthWeight = pcs && totalWeight ? (totalWeight / pcs) : 0;
    updated.final_CutLength_weight = finalCutLengthWeight ? finalCutLengthWeight.toFixed(2) : "";

    // conv_value from selected unit (use string comparison to avoid type mismatch)
    const selectedUnit = unitsList.find((u) => String(u.value) === String(currentFormData.unit_Conv_Id));
    const convValue = selectedUnit?.raw || 1;

    // wt_12_FT = final_CutLength_weight * conv_value / wt_Cut_Length
    updated.wt_12_FT =
      finalCutLengthWeight && wtCutLength && convValue
        ? ((finalCutLengthWeight * convValue) / wtCutLength).toFixed(2)
        : "";

    return updated;
  };

  useEffect(() => {
    if (initialData && initialData.packingDetails) {
      setFormData({
        packing_Date: initialData.packing_Date
          ? initialData.packing_Date.split("T")[0]
          : today,
        totalPcs: initialData.totalPcs || 0,
        total_Weight: initialData.total_Weight || 0,
        packingDetails: initialData.packingDetails.map(row => {
          const updatedRow = {
            bundle_No: row.bundle_No || "0",
            order_No: row.order_No || "",
            dieNumber: row.dieNumber || "",
            die_Id: row.die_Id ?? null,
            batch_Id: row.batch_Id || "",
            casting_No: row.casting_No || "",
            cut_Length: row.cut_Length || "",
            wt_12_FT: row.wt_12_FT || row.wT_12_FT || "",
            wt_Cut_Length: row.wt_Cut_Length || row.wT_Cut_Length || row.cut_Length || "",
            pcs: row.pcs || "",
            total_Weight: row.total_Weight || "",
            series_Name: row.series_Name || "",
            remark: row.remark || "",
            unit_Conv_Id: row.unit_Conv_Id || "",
            final_CutLength_weight: row.final_CutLength_weight || "",
            seq_No: row.seq_No || "",
          };
          return runCalculations(updatedRow, isUnitConvData);
        })
      });
    }
  }, [initialData, isUnitConvData]);

  const handleChange = (value, field, index) => {
    setFormData((prev) => {
      const updatedDetails = [...prev.packingDetails];
      const updatedRow = { ...updatedDetails[index], [field]: value };
      updatedDetails[index] = runCalculations(updatedRow, isUnitConvData);
      return { ...prev, packingDetails: updatedDetails };
    });
  };

  const handleDateChange = (value) => {
    setFormData((prev) => ({ ...prev, packing_Date: value }));
  };

  const addRow = (index) => {
    setFormData((prev) => {
      const updatedDetails = [...prev.packingDetails];
      const newRow = { ...updatedDetails[index] };
      updatedDetails.splice(index + 1, 0, newRow);
      return { ...prev, packingDetails: updatedDetails };
    });
  };

  const deleteRow = (index) => {
    setFormData((prev) => {
      const updatedDetails = [...prev.packingDetails];
      if (updatedDetails.length > 1) {
        updatedDetails.splice(index, 1);
      }
      return { ...prev, packingDetails: updatedDetails };
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  return (
    <Model
      isOpen={true}
      onClose={onCancel}
      name={isEditing ? "Edit Packing" : "Add Packing"}
      widthClass="max-w-[95vw] lg:max-w-[75vw]"
      bodyClass="overflow-hidden flex flex-col"
    >
      <form onSubmit={handleSubmit} className="flex flex-col h-full overflow-hidden">
        {/* Core Bundle Header (Single Fields) */}
        <div className="p-6 border-b border-slate-100 bg-slate-50/50 shrink-0">
          <div className="max-w-xs">
            <InputField
              type="date"
              label="Packing Date"
              value={formData?.packing_Date || ""}
              onChange={(val) => handleDateChange(val)}
              className="rounded-none border-gray-200 focus:border-primary bg-white font-medium"
            />
          </div>
        </div>

        {/* Dynamic Detail Table (All Other Fields Below) */}
        <div className="bg-white border-b border-slate-100 overflow-hidden rounded-none flex-1 flex flex-col min-h-0">
          <div className="overflow-x-auto overflow-y-auto flex-1 min-h-0">
            <table className="w-full text-left border-collapse min-w-[1200px]">
              <thead className="sticky top-0 z-10 bg-gray-50">
                <tr className="bg-gray-50 border-b border-gray-200">

                  <th className="px-3 py-3 text-[10px] font-black text-gray-500 uppercase tracking-[1.5px] w-28">Bundle</th>
                  <th className="px-3 py-3 text-[10px] font-black text-gray-500 uppercase tracking-[1.5px] w-24">Seq</th>
                  <th className="px-3 py-3 text-[10px] font-black text-gray-500 uppercase tracking-[1.5px]">Batch / Casting</th>
                  <th className="px-3 py-3 text-[10px] font-black text-gray-500 uppercase tracking-[1.5px]">Order / Die</th>
                  <th className="px-3 py-3 text-[10px] font-black text-gray-500 uppercase tracking-[1.5px]">Series</th>
                  <th className="px-3 py-3 text-[10px] font-black text-gray-500 uppercase tracking-[1.5px] text-center w-32">Length / Final Cut Length</th>
                  <th className="px-3 py-3 text-[10px] font-black text-gray-500 uppercase tracking-[1.5px] w-36">Unit</th>
                  <th className="px-3 py-3 text-[10px] font-black text-gray-500 uppercase tracking-[1.5px] text-center w-32">Total Wt (KG)</th>
                  <th className="px-3 py-3 text-[10px] font-black text-gray-500 uppercase tracking-[1.5px] text-center w-28">Pcs</th>
                  <th className="px-3 py-3 text-[10px] font-black text-gray-500 uppercase tracking-[1.5px] text-center">WT/12 FT</th>
                  <th className="px-3 py-3 text-[10px] font-black text-gray-500 uppercase tracking-[1.5px] text-center">Cut Length</th>
                  <th className="px-3 py-3 text-[10px] font-black text-gray-500 uppercase tracking-[1.5px] w-48">Remark</th>
                  <th className="px-3 py-3 text-[10px] font-black text-gray-500 uppercase tracking-[1.5px] w-24">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {formData?.packingDetails?.map((row, index) => {
                  const selectedUnit = isUnitConvData.find((u) => String(u.value) === String(row.unit_Conv_Id));
                  return (
                    <tr key={index} className="hover:bg-gray-50/30 transition-colors">
                      {/* Bundle No Input */}
                      <td className="px-3 py-4 text-[12px] font-black text-gray-900">
                        {row.bundle_No || 0}
                      </td>

                      {/* Seq No Display */}
                      <td className="px-3 py-4 text-[12px] font-black text-slate-500 font-mono">
                        {row.seq_No || "-"}
                      </td>

                      {/* Batch & Casting No */}
                      <td className="px-3 py-4 text-[12px] font-black text-gray-900">
                        {row.batch_Id || "-"}
                        <span className="block text-[12px] text-gray-500 font-normal mt-0.5">{row.casting_No || "-"}</span>
                      </td>

                      {/* Order & Die Number */}
                      <td className="px-3 py-4 text-[12px] text-gray-900 font-medium">
                        {row.order_No || "-"}
                        <span className="block text-[10px] font-black text-primary uppercase tracking-wider mt-0.5">{row.dieNumber || "-"}</span>
                      </td>

                      {/* Series & Length */}
                      <td className="px-3 py-4 text-[12px] text-gray-900 font-medium">
                        {row.series_Name || "-"}
                      </td>

                      {/* Final Cut Length Input */}
                      <td className="px-2 py-2">
                        <span className="block text-[12px] font-bold text-primary mt-0.5 text-center">{row.cut_Length || "-"}</span>
                        <input
                          type="number"
                          step="any"
                          required
                          className="w-full bg-gray-50 border border-gray-200 px-2.5 py-1.5 text-[12px] font-black text-center focus:border-primary focus:bg-white outline-none transition-all rounded-none font-mono"
                          value={row.wt_Cut_Length ?? ""}
                          onChange={(e) => handleChange(e.target.value, "wt_Cut_Length", index)}
                        />

                      </td>

                      {/* Unit Select Dropdown */}
                      <td className="px-2 py-2">
                        <select
                          required
                          className="w-full bg-gray-50 border border-gray-200 px-2 py-1.5 text-[12px] font-black focus:border-primary focus:bg-white outline-none transition-all rounded-none"
                          value={row.unit_Conv_Id ?? ""}
                          onChange={(e) => handleChange(e.target.value, "unit_Conv_Id", index)}
                        >
                          <option value="">Select...</option>
                          {isUnitConvData.map((opt) => (
                            <option key={opt.value} value={opt.value}>
                              {opt.label}
                            </option>
                          ))}
                        </select>
                        {selectedUnit && (
                          <div className="mt-1 text-[8px] font-bold text-primary uppercase tracking-wider">
                            Unit Conv.: {selectedUnit.raw}
                          </div>
                        )}
                      </td>

                      {/* Total Weight Input */}
                      <td className="px-2 py-2">
                        <input
                          type="number"
                          step="any"
                          required
                          className="w-full bg-gray-50 border border-gray-200 px-2.5 py-1.5 text-[12px] font-black text-center focus:border-primary focus:bg-white outline-none transition-all rounded-none font-mono text-emerald-700"
                          value={row.total_Weight ?? ""}
                          onChange={(e) => handleChange(e.target.value, "total_Weight", index)}
                        />
                      </td>

                      {/* Pcs Input */}
                      <td className="px-2 py-2">
                        <input
                          type="number"
                          required
                          className="w-full bg-gray-50 border border-gray-200 px-2.5 py-1.5 text-[12px] font-black text-center focus:border-primary focus:bg-white outline-none transition-all rounded-none font-mono"
                          value={row.pcs ?? ""}
                          onChange={(e) => handleChange(e.target.value, "pcs", index)}
                        />
                      </td>

                      {/* WT/12 FT (Computed) */}
                      <td className="px-3 py-4 text-center text-[12px] font-mono font-black text-gray-900">
                        {row.wt_12_FT || "-"}
                      </td>

                      {/* Cut Length Wt (Computed) */}
                      <td className="px-3 py-4 text-center text-[12px] font-mono font-black text-primary">
                        {row.final_CutLength_weight || "-"}
                      </td>

                      {/* Remark Input */}
                      <td className="px-2 py-2">
                        <input
                          type="text"
                          className="w-full bg-gray-50 border border-gray-200 px-3 py-1.5 text-[12px] font-medium focus:border-primary focus:bg-white outline-none transition-all rounded-none"
                          placeholder="Add remark..."
                          value={row.remark ?? ""}
                          onChange={(e) => handleChange(e.target.value, "remark", index)}
                        />
                      </td>
                      <td className="px-3 py-4 text-center">
                        <div className="flex gap-2 justify-center">
                          <button
                            type="button"
                            onClick={() => addRow(index)}
                            className="w-6 h-6 flex items-center justify-center rounded-full bg-emerald-50 text-emerald-600 hover:bg-emerald-500 hover:text-white transition-colors"
                            title="Add/Duplicate Row"
                          >
                            <MdAdd />
                          </button>
                          <button
                            type="button"
                            onClick={() => deleteRow(index)}
                            disabled={formData.packingDetails.length === 1}
                            className={`w-6 h-6 flex items-center justify-center rounded-full transition-colors ${formData.packingDetails.length === 1
                              ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                              : "bg-red-50 text-red-500 hover:bg-red-500 hover:text-white"
                              }`}
                            title="Delete Row"
                          >
                            <MdDelete />
                          </button>
                        </div>
                      </td>
                    </tr>
                  )
                })}
              </tbody>

            </table>
          </div>
        </div>
        <div ref={tableBottomRef} />

        <FormFooter
          onCancel={onCancel}
          onSubmit={handleSubmit}
          submitText="Save"
          isSaving={isSaving}
          leftContent={
            <div className="flex items-center gap-5 bg-slate-50 border border-slate-200/60 px-4 py-1.5 rounded-sm">
              <div className="flex flex-col">
                <span className="text-[8px] text-gray-400 font-black uppercase tracking-[1.5px] leading-none mb-0.5">Total Bundles</span>
                <span className="text-[14px] font-black text-slate-800 font-mono leading-none mt-2">
                  {formData?.packingDetails?.length || 0}
                </span>
              </div>
              <div className="w-[1px] bg-slate-200 h-5" />
              <div className="flex flex-col">
                <span className="text-[8px] text-gray-400 font-black uppercase tracking-[1.5px] leading-none mb-0.5">Total Pcs</span>
                <span className="text-[14px] mt-2 font-black text-primary font-mono leading-none">
                  {formData?.packingDetails?.reduce((sum, r) => sum + (Number(r.pcs) || 0), 0).toLocaleString()} | {formData.totalPcs}
                </span>
              </div>
              <div className="w-[1px] bg-slate-200 h-5" />
              <div className="flex flex-col">
                <span className="text-[8px] text-gray-400 font-black uppercase tracking-[1.5px] leading-none mb-0.5">Total Weight</span>
                <span className="text-[14px] mt-2 font-black text-emerald-700 font-mono leading-none">
                  {formData?.packingDetails?.reduce((sum, r) => sum + (parseFloat(r.total_Weight) || 0), 0).toFixed(3)}{" "} | {formData.total_Weight}
                  {/* <span className="text-[9px] text-gray-400 font-bold">KG</span> */}
                </span>
              </div>
            </div>
          }
        />
      </form>
    </Model>
  );
};

export default PackingForm;
